// Multi-currency helpers — parses a GBP price string and converts/formats.

export const CURRENCIES = [
  { code: "GBP", symbol: "£", label: "GBP" },
  { code: "EUR", symbol: "€", label: "EUR" },
  { code: "USD", symbol: "$", label: "USD" },
  { code: "CAD", symbol: "C$", label: "CAD" },
  { code: "NGN", symbol: "₦", label: "NGN" },
];

export const COUNTRY_TO_CURRENCY = {
  GB: "GBP", UK: "GBP", IE: "EUR", FR: "EUR", DE: "EUR", ES: "EUR", IT: "EUR",
  NL: "EUR", BE: "EUR", PT: "EUR", AT: "EUR", FI: "EUR", GR: "EUR", LU: "EUR",
  MT: "EUR", CY: "EUR", EE: "EUR", LV: "EUR", LT: "EUR", SI: "EUR", SK: "EUR",
  US: "USD", CA: "CAD", NG: "NGN",
};

// Fallback rates (1 GBP = X target). Used if live API fails.
const FALLBACK_RATES = {
  GBP: 1,
  EUR: 1.17,
  USD: 1.27,
  CAD: 1.73,
  NGN: 1900,
};

const RATES_CACHE_KEY = "demj_rates_v1";
const RATES_TTL_MS = 12 * 60 * 60 * 1000; // 12h

export async function fetchRates() {
  try {
    const cached = JSON.parse(localStorage.getItem(RATES_CACHE_KEY) || "null");
    if (cached && Date.now() - cached.ts < RATES_TTL_MS && cached.rates) {
      return cached.rates;
    }
  } catch {
    // ignore cache parse errors
  }
  try {
    const res = await fetch("https://open.er-api.com/v6/latest/GBP");
    const data = await res.json();
    if (data && data.rates) {
      const rates = {
        GBP: 1,
        EUR: data.rates.EUR || FALLBACK_RATES.EUR,
        USD: data.rates.USD || FALLBACK_RATES.USD,
        CAD: data.rates.CAD || FALLBACK_RATES.CAD,
        NGN: data.rates.NGN || FALLBACK_RATES.NGN,
      };
      try {
        localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ ts: Date.now(), rates }));
      } catch {
        // localStorage may be unavailable
      }
      return rates;
    }
  } catch {
    // network failure — fall back below
  }
  return FALLBACK_RATES;
}

export async function detectCurrency() {
  try {
    const res = await fetch("https://ipapi.co/json/", { cache: "no-store" });
    const data = await res.json();
    const country = (data?.country_code || data?.country || "").toUpperCase();
    if (country && COUNTRY_TO_CURRENCY[country]) return COUNTRY_TO_CURRENCY[country];
  } catch {
    // ignore — try locale fallback
  }
  try {
    const lang = (navigator.language || "").toUpperCase();
    const country = lang.split("-")[1] || lang.split("_")[1];
    if (country && COUNTRY_TO_CURRENCY[country]) return COUNTRY_TO_CURRENCY[country];
  } catch {
    // ignore — return default
  }
  return "GBP";
}

// Parse the numeric GBP amount out of a free-form price string.
// Examples:
//   "From £120"  -> { prefix: "From", amount: 120 }
//   "£250"       -> { prefix: "",     amount: 250 }
//   "120"        -> { prefix: "",     amount: 120 }
//   "From £1,250.00" -> { prefix: "From", amount: 1250 }
export function parseGBP(value) {
  if (value == null) return null;
  const s = String(value);
  const match = s.match(/(-?\d[\d,]*(\.\d+)?)/);
  if (!match) return null;
  const amount = parseFloat(match[1].replace(/,/g, ""));
  if (Number.isNaN(amount)) return null;
  // Prefix = words before the first digit/symbol — e.g. "From", "Starting at"
  const prefixMatch = s.match(/^([^\d£$€₦C]*)/);
  let prefix = (prefixMatch?.[1] || "").trim();
  // Drop trailing currency words
  prefix = prefix.replace(/(GBP|EUR|USD|CAD|NGN)$/i, "").trim();
  return { prefix, amount };
}

// Round in the merchant's favour: small amounts keep 2 decimals (ceil to nearest cent),
// larger amounts ceil to the nearest whole / nearest 5 so a price never converts down.
function niceRound(value, code) {
  if (code === "GBP") {
    return Math.round(value * 100) / 100;
  }
  if (code === "NGN") {
    if (value >= 10000) return Math.ceil(value / 100) * 100;
    if (value >= 1000) return Math.ceil(value / 50) * 50;
    return Math.ceil(value);
  }
  if (value >= 1000) return Math.ceil(value / 5) * 5;
  if (value >= 100) return Math.ceil(value);
  // Under 100 — keep 2dp, always round UP to the nearest cent.
  return Math.ceil(value * 100) / 100;
}

export function convertAndFormat(gbpAmount, currencyCode, rates) {
  if (gbpAmount == null) return "";
  const rate = (rates && rates[currencyCode]) || FALLBACK_RATES[currencyCode] || 1;
  const converted = niceRound(gbpAmount * rate, currencyCode);
  const cur = CURRENCIES.find((c) => c.code === currencyCode) || CURRENCIES[0];
  // Use 2 decimal places for small non-NGN amounts so €2.34 doesn't become €2.
  const showCents = currencyCode !== "NGN" && converted < 100 && converted % 1 !== 0;
  try {
    const formatted = new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: showCents ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0,
    }).format(converted);
    return `${cur.symbol}${formatted}`;
  } catch {
    return `${cur.symbol}${converted}`;
  }
}

// Format a full price string preserving any "From"-style prefix.
export function formatGBPInCurrency(priceGbpString, currencyCode, rates) {
  const parsed = parseGBP(priceGbpString);
  if (!parsed) return priceGbpString || "";
  const display = convertAndFormat(parsed.amount, currencyCode, rates);
  return parsed.prefix ? `${parsed.prefix} ${display}` : display;
}
